import {
  LOCATION_RECENCY_BANDS,
  type LocationIntelPointDto,
  type LocationIntelPointsPageDto,
  type LocationRecencyBand,
} from '@pams/shared';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [LOCATION-INTEL] LOCATIONAL INTEL — THE PURE HALF OF THE PAGE: ITS PATHS, AND HOW A HOLDER'S WHOLE
 *  SCOPE IS READ A PAGE AT A TIME.
 *
 *  ⚠️ THE SERVER PAGES AND THIS WALKS THE PAGES. §7: anything that grows with the officer count is
 *  paginated with no exception, so `/location-intel/points` answers at most `LOCATION_INTEL_PAGE_MAX`
 *  officers a request. A map is only true when it holds every dot, so this reads page after page — IN
 *  ORDER, never in parallel, so a holder with ten thousand officers costs ten requests one after
 *  another and never ten at once.
 *
 *  ⚠️ BOUNDED, AND THE BOUND IS SAID ON THE PAGE. At most `LOCATION_INTEL_MAX_PAGES` pages (40 000
 *  officers, against an establishment of about 27 000); past it the page states how many dots it drew
 *  and of how many. A map that drops dots in silence reads as a force that is not there.
 *
 *  ⚠️ ONE DOT PER OFFICER. The pages are `_id`-ordered with skip and limit, so an officer who reports a
 *  first location between two reads shifts every later row by one and one id can arrive twice; it is
 *  kept once. (The same shift can push one officer past a page edge unseen until the next refresh —
 *  the price of skip and limit, and a refresh away from right.)
 *
 *  ⚠️ NO NAME, RANK OR PHONE EVER PASSES THROUGH HERE — the points carry none (the server's choice,
 *  pinned by `locationIntel.test.ts`). The officer card is a separate read, made on a click.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** The API's paths. The web route happens to share the prefix. */
export const LOCATION_INTEL_POINTS_PATH = '/location-intel/points';
export const LOCATION_INTEL_CONFIG_PATH = '/location-intel/config';
export const locationIntelOfficerPath = (officerId: string): string =>
  `/location-intel/officers/${encodeURIComponent(officerId)}`;

/** The most pages one read walks. See the header: past it the page says so. */
export const LOCATION_INTEL_MAX_PAGES = 40;

export type LocationIntelPoints = {
  points: LocationIntelPointDto[];
  /** The server's count of officers in reach with a location, from the LAST page read. */
  total: number;
  /**
   * [LOCATION-INTEL.1] Whether the read stopped at `LOCATION_INTEL_MAX_PAGES` with pages still to come — the BOUND.
   * Fewer points than `total` without it is the skip-and-limit shift above, which a refresh puts right; the page
   * words the two differently, because only one of them is a refresh away from right.
   */
  capped: boolean;
};

/** One page of points, as the caller fetches it — `api.get` on the page, a stub in a test. */
export type ReadPointsPage = (page: number) => Promise<LocationIntelPointsPageDto>;

export const readAllPoints = async (readPage: ReadPointsPage): Promise<LocationIntelPoints> => {
  const byId = new Map<string, LocationIntelPointDto>();
  let total = 0;
  let capped = false;
  for (let page = 1; page <= LOCATION_INTEL_MAX_PAGES; page += 1) {
    const answer = await readPage(page);
    total = answer.total;
    for (const point of answer.items) {
      if (!byId.has(point.id)) byId.set(point.id, point);
    }
    if (answer.items.length === 0 || page >= answer.pages) break;
    if (page === LOCATION_INTEL_MAX_PAGES) capped = true;
  }
  return { points: [...byId.values()], total, capped };
};

/** How many dots are in each band — the sentence beside the map, which the canvas cannot say. */
export const countByBand = (points: readonly LocationIntelPointDto[]): Record<LocationRecencyBand, number> => {
  const counts = Object.fromEntries(LOCATION_RECENCY_BANDS.map((band) => [band, 0])) as Record<
    LocationRecencyBand,
    number
  >;
  for (const point of points) counts[point.band] += 1;
  return counts;
};
